import { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

const mod = (i, n) => ((i % n) + n) % n;

export default function Lightbox({ images, index, onClose, onNavigate }) {
  const open = index !== null && index !== undefined;

  const prev = useCallback(() => onNavigate(mod(index - 1, images.length)), [index, images.length, onNavigate]);
  const next = useCallback(() => onNavigate(mod(index + 1, images.length)), [index, images.length, onNavigate]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, prev, next, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={onClose}
          data-testid="lightbox-overlay"
        >
          <AnimatePresence mode="wait">
            <motion.img
              key={images[index]}
              src={images[index]}
              alt=""
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="max-w-[92vw] max-h-[92vh] object-contain select-none"
              onClick={(e) => e.stopPropagation()}
              draggable="false"
              onContextMenu={(e) => e.preventDefault()}
              data-testid="lightbox-image"
            />
          </AnimatePresence>

          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            data-testid="lightbox-prev"
            aria-label="Photo précédente"
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 p-3 text-[#FBF8EF]/70 hover:text-[#FBF8EF] transition-colors duration-300 focus:outline-none"
          >
            <ChevronLeft size={34} strokeWidth={1} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            data-testid="lightbox-next"
            aria-label="Photo suivante"
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 p-3 text-[#FBF8EF]/70 hover:text-[#FBF8EF] transition-colors duration-300 focus:outline-none"
          >
            <ChevronRight size={34} strokeWidth={1} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
